import React, { useState } from 'react'
import { useQuery } from 'jsonapi-react'
import { Search, X } from 'react-feather'

import Dropdown from 'app/theme/Dropdown'
import { Modal } from 'lib/modal'
import Select from 'app/theme/forms/Select'

import locations from '../locations'

import ListingEquipmentFilter from './ListingEquipmentFilter'
import ListingFuelTypeFilter from './ListingFuelTypeFilter'
import ListingMileageFilter from './ListingMileageFilter'
import ListingPriceFilter from './ListingPriceFilter'
import ListingVehicleTypeFilter from './ListingVehicleTypeFilter'
import ListingYearFilter from './ListingYearFilter'

/* VEHICLE TYPE */
const vehicleTypeMapping = {
  truck: "Food Truck",
  cart: "Cart",
  trailer: "Trailer"
}

export default function ListingFilterBar({ params, updatePageUrl, isOpen, closeModal, stateSlug, filtersCount }) {

  const { filters } = params
  const [localFiltersCopy, setLocalFiltersCopy] = useState({
    ...params,
    filters: {
      search: filters.search,
      fuel_type: filters.fuel_type ? filters.fuel_type : [],
      min_price: filters.min_price ? parseInt(filters.min_price) / 1000 : 0,
      max_price: filters.max_price ? parseInt(filters.max_price) / 1000 : 100,
      min_year: filters.min_year,
      max_year: filters.max_year,
      min_mileage: filters.min_mileage,
      max_mileage: filters.max_mileage,
      equipment: filters.equipment ? filters.equipment : []
    }
  })

  /* SEARCH */
  const [searchValue, setSearchValue] = useState(filters.search)

  const onSearchChange = (search) => {
    params.filters = {
      ...params.filters,
      search
    }
    updatePageUrl()
  }

  /* MIN/MAX PRICE */
  const setPriceRangeValue = (v) => {
    setLocalFiltersCopy({
      ...localFiltersCopy,
      filters: {
        ...localFiltersCopy.filters,
        min_price: v.min,
        max_price: v.max
      }
    })
  }

  /* VEHICLE TYPE */
  const setVehicleType = (checked, vt) => {
    let types = localFiltersCopy.vehicle_type || []
    setLocalFiltersCopy({
      ...localFiltersCopy,
      vehicle_type: checked ? types.concat(vt) : types.filter(t => t !== vt)
    })
  }

  /* FUEL TYPE */
  const setFuelType = (checked, ft) => {
    let types = localFiltersCopy.filters.fuel_type || []
    setLocalFiltersCopy({
      ...localFiltersCopy,
      filters: {
        ...localFiltersCopy.filters,
        fuel_type: checked ? types.concat(ft) : types.filter(t => t !== ft)
      }
    })
  }

  /* MIN/MAX Year */
  const setYearRange = (field, value) => {
    setLocalFiltersCopy({
      ...localFiltersCopy,
      filters: {
        ...localFiltersCopy.filters,
        [field]: value
      }
    })
  }

  /* MIN/MAX MILEAGE */
  const setMileageRange = (field, value) => {
    setLocalFiltersCopy({
      ...localFiltersCopy,
      filters: {
        ...localFiltersCopy.filters,
        [field]: value
      }
    })
  }

  /* EQUIPMENT */
  const featureCategories = useQuery(['vehicle_feature_categories', {
    include: ['vehicle_features'],
    fields: {
      vehicle_feature_categories: [
        'name',
        'category_id'
      ]
    }
  }])

  const updateVehicleFeatures = (feature, checked) => {
    const equipment = localFiltersCopy.filters.equipment || []
    setLocalFiltersCopy({
      ...localFiltersCopy,
      filters: {
        ...localFiltersCopy.filters,
        equipment: checked ? equipment.concat(feature.id) : equipment.filter(e => e !== feature.id)
      }
    })
  }

  /* UPDATE ALL FILTERS (except for state/page/sort) */
  const updateFilters = () => {
    const sortedEquipment = localFiltersCopy.filters.equipment?.sort((a, b) => parseInt(a) - parseInt(b)) || []
    const f = localFiltersCopy.filters
    params.vehicle_type = localFiltersCopy.vehicle_type && localFiltersCopy.vehicle_type?.length ? localFiltersCopy.vehicle_type.sort() : undefined
    params.filters = {
      search: f.search,
      min_price: f.min_price === 0 ? undefined : f.min_price * 1000,
      max_price: f.max_price === 100 ? undefined : f.max_price * 1000,
      fuel_type: f.fuel_type && f.fuel_type?.length ? f.fuel_type : undefined,
      min_mileage: f.min_mileage ? f.min_mileage : undefined,
      max_mileage: f.max_mileage ? f.max_mileage : undefined,
      min_year: f.min_year ? f.min_year : undefined,
      max_year: f.max_year ? f.max_year : undefined,
      equipment: sortedEquipment.length ? sortedEquipment : undefined
    }
    updatePageUrl()
  }

  return (
    <div className="w-full bg-white my-10 py-8 border shadow-sm relative">
      <div className="container flex flex-wrap lg:flex-nowrap">

        {/* Search bar */}
        <div className="flex flex-auto lg:flex-1 w-full flex-shrink-0 lg:flex-shrink-1 mb-0 md:mb-4 lg:mb-0">
          <div className="relative flex flex-1 items-center">
            <Search
              size={16}
              className="absolute left-0 ml-2"
            />
            <input
              type="text"
              placeholder='Search by keyword'
              value={searchValue || ''}
              onKeyDown={event => {
                if (['Enter', 'Tab'].includes(event.key)) {
                  onSearchChange(searchValue)
                }
              }}
              onChange={event => {
                setSearchValue(event.target.value)
              }}
              className={[
                'outline-none rounded rounded-r-none border btn-white no-hover border-r-0',
                'w-full h-full py-3 md:py-3 px-8 leading-loose text-md'
              ]}
            />
            {searchValue && (
              <button
                type="button"
                children={<X size={12} />}
                onClick={() => {
                  setSearchValue('')
                  onSearchChange('')
                }}
                className={[
                  'rounded bg-gray-200 absolute right-0 mr-2 p-1 rounded-full',
                  'hover:bg-gray-300 focus:outline-none',
                ]}
              />
            )}
          </div>
          <button
            type="button"
            children="Search"
            onClick={() => {
              onSearchChange(searchValue)
            }}
            className={[
              'btn btn-white border border-blue rounded rounded-l-none',
              'px-5'
            ]}
            style={{ 'borderWidth': '1px' }}
          />
        </div>


        <Select
          value={stateSlug ? stateSlug.toUpperCase() : ''}
          options={[
            { label: 'Any Location', value: '' },
          ].concat(locations?.map((location, index) => (
            { label: location.name, value: location.shortCode }
          )))}
          classNames={{
            select: "select-basic w-auto rounded btn-white font-normal px-5 lg:ml-5 h-full",
            div: "my-2 md:my-0 hidden md:inline flex",
          }}
          onChange={value => {
            params.vehicle_state = value ? value.toLowerCase() : undefined
            updatePageUrl()
          }}
        />


        <Dropdown className="hidden md:flex">
          <Dropdown.Toggle
            className={[
              'btn btn-white rounded px-5 ml-5'
            ]}
            style={{ 'borderWidth': '1px' }}
          >
            {!params.filters.min_price && !params.filters.max_price ?
              "Any Price" :
              `${params.filters.min_price ? '$' + parseInt(params.filters.min_price) / 1000 + 'k' : '$0'} - $${params.filters.max_price && params.filters.max_price <= 100000 ? parseInt(params.filters.max_price) / 1000 : '100'}k${params.filters.max_price >= 100000 || !params.filters.max_price ? '+' : ''}`
            }
          </Dropdown.Toggle>
          <Dropdown.Content
            className={[
              'bg-white border shadow rounded p-6',
              'mt-1 font-light right-0',
            ]}
            style={{ width: "350px", top: "100%" }}
          >
            <ListingPriceFilter rangeValue={{ min: localFiltersCopy.filters.min_price, max: localFiltersCopy.filters.max_price } } setRangeValue={setPriceRangeValue} />

            <div className="flex justify-between mt-5">
              <Dropdown.Item
                className="btn btn-gray rounded px-5 py-2"
                onClick={() => {
                  setPriceRangeValue({
                    min: params.filters.min_price ? parseInt(params.filters.min_price) / 1000 : 0,
                    max: params.filters.max_price ? parseInt(params.filters.max_price) / 1000 : 100
                  })
                }}>
                Cancel
              </Dropdown.Item>
              <button
                children="Update"
                className="btn btn-white rounded px-5 py-2"
                onClick={() => {
                  params.filters = {
                    ...params.filters,
                    min_price: localFiltersCopy.filters.min_price === 0 ? undefined : localFiltersCopy.filters.min_price * 1000,
                    max_price: localFiltersCopy.filters.max_price === 100 ? undefined : localFiltersCopy.filters.max_price * 1000
                  }
                  updatePageUrl()
                }}
              />
            </div>
          </Dropdown.Content>
        </Dropdown>

        <Dropdown className="hidden md:flex">
          <Dropdown.Toggle
            className={[
              'btn btn-white border rounded px-5 ml-5'
            ]}
            style={{ 'borderWidth': '1px' }}
          >
            {params.vehicle_type ?
              params.vehicle_type?.map(vt => vehicleTypeMapping[vt]).join(', ')
              :
              "Vehicle Types"
            }
          </Dropdown.Toggle>
          <Dropdown.Content
            className={[
              'bg-white border shadow rounded p-6',
              'mt-1 font-light right-0',
            ]}
            style={{ width: "350px", top: "100%" }}
          >
            <ListingVehicleTypeFilter vehicleType={localFiltersCopy.vehicle_type} setVehicleType={setVehicleType} />

            <div className="flex justify-between mt-5">
              <Dropdown.Item
                className="btn btn-gray rounded px-5 py-2"
                onClick={() => {
                  setLocalFiltersCopy({
                    ...localFiltersCopy,
                    vehicle_type: params.vehicle_type
                  })
                }}
              >
                Cancel  
              </Dropdown.Item>
            
              <button
                children="Update"
                className="btn btn-white rounded px-5 py-2"
                onClick={() => {
                  params.vehicle_type = localFiltersCopy.vehicle_type && localFiltersCopy.vehicle_type?.length ? localFiltersCopy.vehicle_type.sort() : undefined,
                  updatePageUrl()
                }}
              />
            </div>
          </Dropdown.Content>
        </Dropdown>

        <Dropdown className="hidden md:flex flex-1" style={{ 'position': 'static' }}>
          <Dropdown.Toggle
            className={[
              'btn btn-white rounded px-5 ml-5 w-full'
            ]}
            style={{ 'borderWidth': '1px' }}
          >
            More Filters {filtersCount.partial > 0 ? `(${filtersCount.partial})` : "" }
            </Dropdown.Toggle>
          <Dropdown.Content
            className={[
              'bg-white shadow rounded p-6',
              'font-light right-0 w-full',
            ]}
            style={{ top: "100%" }}
          >
            <div className="container">
              <ListingEquipmentFilter equipment={localFiltersCopy.filters.equipment} featureCategories={featureCategories} updateVehicleFeatures={updateVehicleFeatures} />
              <div className="grid grid-cols-5 gap-12 mt-5">
                <div>
                  <ListingYearFilter start={localFiltersCopy.filters.min_year} end={localFiltersCopy.filters.max_year} update={setYearRange} />
                </div>
                <div>
                  <ListingFuelTypeFilter fuelType={localFiltersCopy.filters.fuel_type} setFuelType={setFuelType} />
                </div>
                <div>
                  <ListingMileageFilter start={localFiltersCopy.filters.min_mileage} end={localFiltersCopy.filters.max_mileage} update={setMileageRange} />
                </div>
              </div>
              <div className="w-full flex justify-end">
                {filtersCount > 0 && (
                  <button
                    children="Reset Filters"
                    className="btn btn-white rounded px-5 py-2 mr-2"
                    onClick={() => {
                      closeModal()
                      setParams({
                        ...params,
                        vehicle_type: undefined,
                        filter: {
                          search: undefined,
                          fuel_type: undefined,
                          min_price: undefined,
                          max_price: undefined,
                          min_year: undefined,
                          max_year: undefined,
                          min_mileage: undefined,
                          max_mileage: undefined,
                          equipment: undefined
                        }
                      })
                    }}
                  />
                )}

                <Dropdown.Item
                  className="btn btn-gray rounded px-5 py-2 mr-2"
                  onClick={() => {
                    setLocalFiltersCopy({
                      ...params,
                      filter: {
                        ...localFiltersCopy.filters,
                        min_price: params.filters.min_price ? parseInt(params.filters.min_price) / 1000 : 0,
                        max_price: params.filters.max_price ? parseInt(params.filters.max_price) / 1000 : 100 
                      }
                    })
                  }}
                >
                  Cancel
                </Dropdown.Item>
                <button
                  children="Update"
                  className="btn btn-white rounded px-5 py-2"
                  onClick={updateFilters}
                />
              </div>
            </div>
          </Dropdown.Content>
        </Dropdown>
      </div>




      <Modal
        isOpen={isOpen}
        closeModal={closeModal}
        className="bg-white relative w-full h-full max-w-2xl lg:m-5 p-8 lg:p-10 lg:rounded"
      >
        <button
          children={<X size={16} className="m-3" />}
          onClick={closeModal}
          className={[
            'btn absolute top-0 right-0 z-50 text-gray-900',
            'flex justify-center items-center',
            'opacity-50 hover:opacity-100'
          ]}
        />
        <h2 className="text-xl text-black font-bold mb-4">Filter By</h2>

        <h3 className="text-md text-black font-bold mb-4">Location</h3>
        <Select
          value={stateSlug ? stateSlug.toUpperCase() : undefined}
          options={[
            { label: 'Any Location', value: '' },
          ].concat(locations?.map((location, index) => (
            { label: location.name, value: location.shortCode }
          )))}
          classNames={{
            select: "select-basic w-auto rounded border-blue text-blue font-normal px-5",
            div: "my-2 md:my-0",
          }}
          onChange={value => {
            params.vehicle_state = value ? value.toLowerCase() : undefined
            updatePageUrl()
          }}
        />
        <ListingPriceFilter rangeValue={{ min: localFiltersCopy.filters.min_price, max: localFiltersCopy.filters.max_price}} setRangeValue={setPriceRangeValue} />
        <ListingVehicleTypeFilter vehicleType={localFiltersCopy.vehicle_type} setVehicleType={setVehicleType} />
        <ListingYearFilter start={localFiltersCopy.filters.min_year} end={localFiltersCopy.filters.max_year} update={setYearRange}/>
        <ListingFuelTypeFilter fuelType={localFiltersCopy.filters.fuel_type} setFuelType={setFuelType} />
        <ListingMileageFilter start={localFiltersCopy.filters.min_mileage} end={localFiltersCopy.filters.max_mileage} update={setMileageRange}/>
        <ListingEquipmentFilter equipment={localFiltersCopy.filters.equipment} featureCategories={featureCategories} updateVehicleFeatures={updateVehicleFeatures} />
        <div className="w-full flex items-center justify-center">

          { filtersCount > 0 && (
            <button
              children="Reset Filters"
              className="btn btn-gray rounded px-5 py-2 mr-2"
              onClick={() => {
                closeModal()
                setParams({
                  ...params,
                  vehicle_type: undefined,
                  filter: {
                    search: undefined,
                    fuel_type: undefined,
                    min_price: undefined,
                    max_price: undefined,
                    min_year: undefined,
                    max_year: undefined,
                    min_mileage: undefined,
                    max_mileage: undefined,
                    equipment: undefined
                  }
                })
              }}
            />
          )}
          <button
            children="Cancel"
            className="btn btn-gray rounded px-5 py-2 mr-2"
            onClick={() => {
              closeModal()
              setLocalFiltersCopy({
                ...params,
                filters: {
                  min_price: params.filters.min_price ? parseInt(params.filters.min_price) / 1000 : 0,
                  max_price: params.filters.max_price ? parseInt(params.filters.max_price) / 1000 : 100,
                }
              })
            }}
          />
          <button
            children="Update Filters"
            className="btn btn-white rounded px-5 py-2"
            onClick={updateFilters}
          />
        </div>
      </Modal>



    </div>
  )
}